import { NavLink, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { NAV } from "@/constants/testIds/clinic";

const links = [
    { to: "/", label: "Home", id: NAV.desktopHome, mid: NAV.mobileHome },
    { to: "/about", label: "About", id: NAV.desktopAbout, mid: NAV.mobileAbout },
    { to: "/services", label: "Services", id: NAV.desktopServices, mid: NAV.mobileServices },
    { to: "/approach", label: "Approach", id: NAV.desktopApproach, mid: NAV.mobileApproach },
    { to: "/faq", label: "FAQ", id: NAV.desktopFaq, mid: NAV.mobileFaq },
    { to: "/contact", label: "Contact", id: NAV.desktopContact, mid: NAV.mobileContact },
];

export default function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const { pathname } = useLocation();

    useEffect(() => setOpen(false), [pathname]);
    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 8);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);
    useEffect(() => {
        document.body.style.overflow = open ? "hidden" : "";
        return () => { document.body.style.overflow = ""; };
    }, [open]);

    return (
        <>
            <header
                className={`fixed top-0 inset-x-0 z-40 transition-[background-color,border-color,backdrop-filter] duration-300 ${
                    scrolled
                        ? "bg-[hsl(var(--bg-chalk))]/85 backdrop-blur-md border-b border-[hsl(var(--border-stone))]"
                        : "bg-transparent border-b border-transparent"
                }`}
            >
                <div className="ihx-container flex items-center justify-between h-[72px]">
                    <Link
                        to="/"
                        data-testid={NAV.logoLink}
                        className="flex items-center gap-3 group"
                    >
                        <span
                            aria-hidden
                            className="w-9 h-9 rounded-full bg-[hsl(var(--brand-ink-deep))] text-[hsl(var(--bg-chalk))] grid place-items-center font-serif text-[14px]"
                            style={{ fontFamily: "Fraunces, serif", fontStyle: "italic" }}
                        >
                            ih
                        </span>
                        <span className="hidden sm:flex flex-col leading-tight">
                            <span className="font-serif text-[17px] text-[hsl(var(--brand-ink-deep))]">
                                Dr. Ishani Hanspal
                            </span>
                            <span className="text-[10.5px] tracking-[0.22em] uppercase text-[hsl(var(--brand-gold-deep))] font-medium">
                                Lifestyle &amp; Metabolic Medicine
                            </span>
                        </span>
                    </Link>

                    {/* Desktop pill nav */}
                    <nav className="hidden lg:flex items-center gap-1 bg-[hsl(var(--bg-white-warm))]/70 backdrop-blur border border-[hsl(var(--border-stone))] rounded-full px-2 py-1.5">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={l.id}
                                end={l.to === "/"}
                                className={({ isActive }) =>
                                    `px-4 py-1.5 rounded-full text-[13px] font-medium transition-colors duration-300 ${
                                        isActive
                                            ? "bg-[hsl(var(--brand-ink-deep))] text-[hsl(var(--bg-chalk))]"
                                            : "text-[hsl(var(--ink-soft))] hover:text-[hsl(var(--brand-ink-deep))]"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </nav>

                    <div className="hidden lg:flex items-center">
                        <Link
                            to="/apply"
                            data-testid={NAV.desktopApplyCta}
                            className="ihx-btn-primary"
                        >
                            Apply for Consultation
                            <span className="ihx-arrow" aria-hidden>→</span>
                        </Link>
                    </div>

                    <button
                        type="button"
                        data-testid={NAV.mobileOpen}
                        aria-label="Open navigation"
                        onClick={() => setOpen(true)}
                        className="lg:hidden text-[hsl(var(--brand-ink-deep))] p-2 -mr-2"
                    >
                        <Menu size={26} strokeWidth={1.4} />
                    </button>
                </div>
            </header>

            {open && (
                <div
                    data-testid={NAV.mobileOverlay}
                    className="fixed inset-0 z-50 lg:hidden ihx-anim-fade-in"
                    style={{
                        background:
                            "radial-gradient(60% 80% at 75% 20%, hsl(var(--brand-gold) / 0.35) 0%, transparent 60%), hsl(var(--brand-ink-deep) / 0.96)",
                        backdropFilter: "blur(14px)",
                        WebkitBackdropFilter: "blur(14px)",
                    }}
                >
                    <div className="flex items-center justify-between h-[72px] px-6 md:px-10">
                        <span className="text-[hsl(var(--bg-stone))]/80 text-[10.5px] tracking-[0.28em] uppercase">
                            Menu
                        </span>
                        <button
                            type="button"
                            data-testid={NAV.mobileClose}
                            aria-label="Close navigation"
                            onClick={() => setOpen(false)}
                            className="text-[hsl(var(--bg-chalk))] p-2 -mr-2"
                        >
                            <X size={28} strokeWidth={1.4} />
                        </button>
                    </div>

                    <nav className="flex flex-col items-center justify-center gap-7 px-6 mt-10">
                        {links.map((l, idx) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={l.mid}
                                end={l.to === "/"}
                                className={({ isActive }) =>
                                    `font-serif text-[36px] leading-tight ihx-anim-fade-up ihx-delay-${Math.min(idx + 1, 5)} ${
                                        isActive
                                            ? "text-[hsl(var(--bg-chalk))]"
                                            : "text-[hsl(var(--bg-chalk))]/75 hover:text-[hsl(var(--bg-chalk))]"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        <Link
                            to="/apply"
                            data-testid={NAV.desktopApplyCta + "-mobile-inline"}
                            className="ihx-btn-primary mt-6"
                            style={{
                                backgroundColor: "hsl(var(--brand-gold))",
                                borderColor: "hsl(var(--brand-gold))",
                            }}
                        >
                            Apply for Consultation
                            <span className="ihx-arrow" aria-hidden>→</span>
                        </Link>
                    </nav>

                    <div className="absolute bottom-10 inset-x-0 text-center text-[hsl(var(--bg-chalk))]/55 text-[12px] tracking-[0.18em] uppercase">
                        Chennai · Gurugram · Virtual
                    </div>
                </div>
            )}
        </>
    );
}
